'use client'
import Link from "next/link";
import Image from "next/image";
import { Button } from "../ui/Button";
import { cn } from "@/lib/utils";
import { useTheme } from "next-themes";
import React from "react";

type LogoProps = {
  size?: "sm" | "md" | "lg";
};

// Base Logo component without theme logic
function BaseLogo({ size = "md", theme }: LogoProps & { theme: 'light' | 'dark' }): React.ReactElement {
  const sizeStyles = {
    sm: {
      image: { width: 32, height: 32 },
      text: "text-base",
    },
    md: {
      image: { width: 48, height: 48 },
      text: "text-2xl",
    },
    lg: {
      image: { width: 64, height: 64 },
      text: "text-3xl",
    },
  };

  const { image, text } = sizeStyles[size];

  return (
    <div className="flex items-center">
      <Button variant="link" size="sm">
        <Link href="/" className={cn('font-light text-white font-majormono flex items-center', text)}>
          {theme === 'dark' ? (
            <Image src="/equation-orange.svg" alt="Qognitive Logo" width={image.width} height={image.height} />
          ) : (
            <Image src="/equation-orange.svg" alt="Qognitive Logo" width={image.width} height={image.height} />
          )}
          <span className="font-bold hidden md:block">ognitive</span>
        </Link>
      </Button>
    </div>
  );
}

// Client-side only wrapper component
export default function Logo(props: LogoProps): React.ReactElement {
  const { resolvedTheme } = useTheme();
  const [mounted, setMounted] = React.useState(false);

  React.useEffect(() => {
    setMounted(true);
  }, []);

  // During SSR, render with light theme to avoid hydration mismatch
  if (!mounted) {
    return <BaseLogo {...props} theme="light" />;
  }

  return <BaseLogo {...props} theme={resolvedTheme as 'light' | 'dark'} />;
} 